import * as React from 'react';
import { showGlobalModal } from 'core/components/GlobalModal';
import { maxDescriptionGenerations } from 'features/DigitalMarketing/constants/formStyles';
import { getApiErrorMessage } from 'features/DigitalMarketing/utils/apiResponse';

type UseAiDescriptionOptions = {
    generateDescription: (description: string) => Promise<string>;
    emptyMessage: string;
    errorMessage?: string;
    requiresDescription?: boolean;
};

export const useAiDescription = ({
    generateDescription,
    emptyMessage,
    errorMessage = 'Unable to generate description',
    requiresDescription = true,
}: UseAiDescriptionOptions) => {
    const [isGeneratingDescription, setIsGeneratingDescription] = React.useState(false);
    const [descriptionGenerationCount, setDescriptionGenerationCount] = React.useState(0);
    const descriptionGenerationsRemaining = maxDescriptionGenerations - descriptionGenerationCount;

    const improveDescription = React.useCallback(async (
        description: string,
        onGenerated: (nextDescription: string) => void,
    ) => {
        if (descriptionGenerationsRemaining <= 0) {
            showGlobalModal('Description generation limit reached.', 'warning');
            return;
        }

        if (requiresDescription && !description.trim()) {
            showGlobalModal(emptyMessage, 'warning');
            return;
        }

        setIsGeneratingDescription(true);

        try {
            const nextDescription = await generateDescription(description);

            if (nextDescription) {
                onGenerated(nextDescription);
                setDescriptionGenerationCount(current => Math.min(current + 1, maxDescriptionGenerations));
            } else {
                showGlobalModal(errorMessage, 'error');
            }
        } catch (error: any) {
            showGlobalModal(getApiErrorMessage(error, errorMessage), 'error');
        } finally {
            setIsGeneratingDescription(false);
        }
    }, [descriptionGenerationsRemaining, emptyMessage, errorMessage, generateDescription, requiresDescription]);

    return {
        isGeneratingDescription,
        descriptionGenerationsRemaining,
        improveDescription,
    };
};
